import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import { Link } from '@inertiajs/react';
import { usePage } from '@inertiajs/react';
import { Menu, ShoppingCart } from 'lucide-react';

import { CartSheet } from './shared/CartSheet';
import HeaderGamesNavigation from './shared/HeaderGamesNavigation';
import { MobileUniverseDropdown } from './shared/MobileUniverseDropdown';
import { ProfileDropdown } from './shared/ProfileDropdown';
import SearchBar from './shared/SearchBar';
import { SiteFooterNavigation } from './shared/SiteFooterNavigation';
import { UniverseSelectionDialog } from './shared/UniverseSelectionDialog';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import logoDark from '../assets/logo-dark-theme.svg';
import { useCart } from '../context/cart-context';
import { useSessionSync } from '../hooks/useSessionSync';
import { Routes } from '../models/routes';
import { SelectedUniverseService } from '../services/SelectedUniverseService';
import { CurrentUser, Expansion, Universe } from '../types';
import { AppearanceToggleDropdown } from './AppearanceToggleDropdown';
import logoLight from '../assets/logo-light-theme.svg';
import { useResolvedTheme } from '../hooks/useResolvedTheme';

export interface HeaderRef {
    focusSearch: () => void;
}

type PageProps = {
    universes: Universe[];
    navigationExpansions?: Expansion[];
    currentUniverse?: Universe | null;
    currentExpansion?: Expansion | null;
    currentUser: CurrentUser | null;
    promptForPreferredUniverse?: boolean;
    newNotificationPresent: boolean;
};

const Header = forwardRef<HeaderRef>((_, ref) => {
    useSessionSync();

    const { component, props } = usePage<PageProps>();
    const {
        universes,
        navigationExpansions = [],
        currentUniverse,
        currentExpansion,
        currentUser,
        promptForPreferredUniverse,
        newNotificationPresent,
    } = props;
    const { cartCount } = useCart();
    const searchBarRef = useRef<{ focus: () => void }>(null);
    const isSearchPage = component === 'Search/Index';
    const resolvedTheme = useResolvedTheme();
    const logo = resolvedTheme === 'dark' ? logoDark : logoLight;
    const [showUniverseSelection, setShowUniverseSelection] = useState(false);
    const [showInformationMenu, setShowInformationMenu] = useState(false);
    const informationMenuTriggerRef = useRef<HTMLButtonElement>(null);
    const informationMenuPanelRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            focusSearch: () => {
                if (isSearchPage) {
                    const searchPageInput =
                        document.querySelector<HTMLInputElement>(
                            '[data-testid="search-page-input"]',
                        );
                    searchPageInput?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                    searchPageInput?.focus();
                    return;
                }

                searchBarRef.current?.focus();
            },
        }),
        [isSearchPage],
    );

    useEffect(() => {
        SelectedUniverseService.syncCookieFromStorage();

        setShowUniverseSelection(
            Boolean(currentUser) &&
                Boolean(promptForPreferredUniverse) &&
                SelectedUniverseService.shouldPrompt(universes.length),
        );
    }, [currentUser, promptForPreferredUniverse, universes.length]);

    useEffect(() => {
        if (!showInformationMenu) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;

            setShowInformationMenu(false);
            informationMenuTriggerRef.current?.focus();
        };
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;

            if (
                informationMenuPanelRef.current?.contains(target) ||
                informationMenuTriggerRef.current?.contains(target)
            ) {
                return;
            }

            setShowInformationMenu(false);
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [showInformationMenu]);

    const handleUniverseSelect = (universe: Universe) => {
        SelectedUniverseService.setPreference(universe.slug);
        setShowUniverseSelection(false);
        window.location.reload();
    };

    return (
        <>
            <UniverseSelectionDialog
                open={showUniverseSelection}
                universes={universes}
                onSelect={handleUniverseSelect}
            />

            <header
                className="sticky top-0 z-[90] w-full bg-background md:border-b"
                data-testid="site-header"
            >
                <div className="container flex h-14 items-center justify-between px-3 sm:h-16 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-3">
                        <Button
                            ref={informationMenuTriggerRef}
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="hidden h-9 w-9 lg:inline-flex"
                            aria-label="Додаткове меню"
                            aria-expanded={showInformationMenu}
                            aria-controls="header-information-menu"
                            aria-haspopup="true"
                            onClick={() =>
                                setShowInformationMenu((open) => !open)
                            }
                        >
                            <Menu className="h-5 w-5" />
                        </Button>

                        <Link href="/" className="flex items-center">
                            <img
                                src={logo}
                                alt="CartOn Logo"
                                width={120}
                                height={32}
                                className="h-7 w-auto sm:h-8"
                            />
                        </Link>
                    </div>
                    <div className="mx-2 min-w-0 flex-1 md:hidden">
                        <div className="flex">
                            <MobileUniverseDropdown
                                universes={universes}
                                currentUniverse={currentUniverse}
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
                        {!isSearchPage && (
                            <div className="relative hidden md:block">
                                <SearchBar ref={searchBarRef} />
                            </div>
                        )}

                        <div className="hidden lg:block">
                            <AppearanceToggleDropdown />
                        </div>

                        {currentUser && (
                            <CartSheet>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="relative h-9 w-9 bg-transparent"
                                >
                                    <ShoppingCart className="h-5 w-5" />
                                    <span className="sr-only">Кошик</span>
                                    {cartCount > 0 && (
                                        <Badge className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center p-0 text-[10px]">
                                            {cartCount}
                                        </Badge>
                                    )}
                                </Button>
                            </CartSheet>
                        )}

                        {!currentUser && (
                            <Button
                                size="sm"
                                className="h-8 px-3 text-xs md:hidden"
                                asChild
                            >
                                <Link href={Routes.SignIn}>Увійти</Link>
                            </Button>
                        )}

                        {currentUser ? (
                            <ProfileDropdown
                                currentUser={currentUser}
                                newNotificationPresent={newNotificationPresent}
                            />
                        ) : (
                            <Button
                                className="hidden h-9 px-3 text-xs md:flex lg:px-4 lg:text-sm"
                                asChild
                            >
                                <Link href={Routes.SignIn}>{'Увійти'}</Link>
                            </Button>
                        )}
                    </div>
                </div>

                <HeaderGamesNavigation
                    universes={universes}
                    expansions={navigationExpansions}
                    currentUniverse={currentUniverse}
                    currentExpansion={currentExpansion}
                />

                {showInformationMenu && (
                    <div
                        ref={informationMenuPanelRef}
                        id="header-information-menu"
                        data-testid="header-information-menu"
                        role="navigation"
                        aria-label="Додаткова навігація"
                        className="absolute inset-x-0 top-full max-h-[calc(100vh-6rem)] overflow-y-auto border-y bg-background shadow-xl"
                    >
                        <div className="container px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
                            <SiteFooterNavigation
                                tcgListTestId="header-information-tcg-list"
                                onNavigate={() => setShowInformationMenu(false)}
                            />
                        </div>
                    </div>
                )}
            </header>

            {showInformationMenu && (
                <div
                    className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-[1px]"
                    data-testid="header-information-backdrop"
                    aria-hidden="true"
                />
            )}

            {!isSearchPage && (
                <div className="w-full border-b bg-background px-4 pt-3 pb-3 md:hidden">
                    <SearchBar ref={searchBarRef} />
                </div>
            )}
        </>
    );
});

Header.displayName = 'Header';

export default Header;
